/**
 * StickyRow
 * Memoized row component for the sticky left grain-dimension columns.
 * Renders navigable (pressable) cells for region/market/location,
 * and plain text for non-navigable dimensions.
 */

import React from 'react';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import { GrainKey, GroupedRow } from '../../constants/onePagerConstants';
import { grainKeyToFilterType } from '../../utils/onePagerFormatters';
import type { NavigableFilterType } from '../../types/onePagerTypes';
import { createOnePagerTableStyles } from '../../styles/onePagerTableStyles';
import { useTheme } from '../../../../theme/useTheme';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface StickyRowProps {
	readonly row: GroupedRow;
	readonly index: number;
	readonly grainSelections: readonly GrainKey[];
	readonly cellWidth?: number;
	readonly onNavigateToOverview: (filterType: NavigableFilterType, value: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const StickyRow: React.FC<StickyRowProps> = ({ row, index, grainSelections, cellWidth, onNavigateToOverview }) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
	const styles = createOnePagerTableStyles(isDark, colors);
	const isEven = index % 2 === 0;

	return (
		<View style={[styles.stickyDataRow, isEven && styles.stickyDataRowEven]}>
			{grainSelections.map((grainKey) => {
				const cellValue = row.grainVals[grainKey] || '—';
				const filterType = grainKeyToFilterType(grainKey);
				const isNavigable = filterType !== null;

				if (isNavigable) {
					return (
						<TouchableOpacity
							key={grainKey}
							style={[styles.stickyCell, cellWidth !== undefined && { width: cellWidth }]}
							onPress={() => onNavigateToOverview(filterType, cellValue)}
							activeOpacity={0.6}
							accessibilityLabel={`Navigate to ${cellValue}`}
							accessibilityRole="link"
						>
							<Text style={styles.stickyCellText} numberOfLines={1}>
								{cellValue}
							</Text>
						</TouchableOpacity>
					);
				}

				return (
					<View key={grainKey} style={[styles.stickyCell, cellWidth !== undefined && { width: cellWidth }]}>
						<Text
							style={[styles.stickyCellText, styles.stickyCellTextNonLink]}
							numberOfLines={1}
						>
							{cellValue}
						</Text>
					</View>
				);
			})}
		</View>
	);
};

StickyRow.displayName = 'StickyRow';

export default React.memo(StickyRow);
